<template>
  <div v-if="!platform || hasPlatformRequest" class="loading-screen">
    <b-spinner />
  </div>

  <div v-else @click="getAudiosReady()" id="app" class="h-100" :class="[skinClasses]">
    <loading id="loading-screen" :data="platform" />

    <news-warning v-if="getNewsState" :warning="warning" />

    <dial-bar />

    <modal-carousel v-if="modalCarouselState" />

    <b-modal-central />

    <norification-toast-holder />

    <task-notification-cards />

    <router-view />
  </div>
</template>

<script>
import { BSpinner } from "bootstrap-vue";
import firebase from "firebase/compat/app";
import "firebase/compat/messaging";
import moment from "moment";
import Vue from "vue";
import VueCookie from "vue-cookie";

import BModalCentral from "./views/components/BModalCentral.vue";
import DialBar from "./views/components/External Phone/DialBar.vue";
import Loading from "../src/views/components/Loading.vue";
import ModalCarousel from "./views/components/ModalCarousel.vue";
import NorificationToastHolder from "@/views/components/NotificationToastHolder.vue";
import TaskNotificationCards from "@/views/components/TaskNotificationCards.vue";
import store from "@/store";
import useAppConfig from "@core/app-config/useAppConfig";

import { onMessage, getToken } from "firebase/messaging";
import { $themeColors, $themeBreakpoints, $themeConfig } from "@themeConfig";
import { useCssVar } from "@vueuse/core";
import { mapActions } from "vuex";

Vue.use(VueCookie);

export default {
  components: {
    BSpinner,

    BModalCentral,
    DialBar,
    Loading,
    ModalCarousel,
    NorificationToastHolder,
    TaskNotificationCards,
  },

  data() {
    return {
      skinClasses: [],
      warning: null,

      fbToken: null,
      broadcastChannel: null,

      hasPlatformRequest: true,

      omnichannelSocket: null,
      omniSocketReconnectAttempts: 0,
    };
  },

  computed: {
    platform() {
      return this.$store.getters["whiteLabel/getPlatform"];
    },

    defaultPlatform() {
      return this.$store.getters["whiteLabel/getDefaultPlatformData"];
    },

    user() {
      return this.$store.getters["app/getUserData"];
    },

    getNewsState() {
      return this.$store.state.newsWarning.isOpen;
    },

    allWarnings() {
      return this.$store.getters["newsWarning/getAllWarnings"];
    },

    updateTick() {
      return this.$store.state.newsWarning.updateTick;
    },

    modalCarouselState() {
      return this.$store.getters["modalCarousel/getCarouselState"];
    },

    isReadyAudio() {
      return this.$store.getters["externalPhone/getIsReadyAudios"];
    },

    updateFirebaseTokenVar() {
      return this.$store.getters["firebase/getUpdateToken"];
    },

    isShowingNotification() {
      return this.$store.getters["firebase/getIsShowingNotification"];
    },

    role() {
      return this.$store.getters["app/getUserRole"];
    },
  },

  beforeCreate() {
    const colors = ["primary", "secondary", "success", "info", "warning", "danger", "light", "dark"];

    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim();
    }

    const breakpoints = ["xs", "sm", "md", "lg", "xl"];

    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2),
      );
    }

    const { isRTL } = $themeConfig.layout;
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
  },

  created() {
    this.setPlatform();
    this.cleanStates();

    this.initialSetup();

    this.checkPermissionForFB();

    this.addMessageListener();

    this.connectOmnichannelWebSocket();

    this.$eventBus.on("hub-auth-success", () => {
      this.connectWebSocket();
    });

    this.$eventBus.on("auth-success", () => {
      this.connectOmnichannelWebSocket();
    });

    this.$eventBus.on("logout", () => {
      this.disconnectOmnichannelWebSocket();
    });
  },

  mounted() {
    this.addGtag();
    this.addClarity();
  },

  beforeDestroy() {
    this.disconnectOmnichannelWebSocket();
    this.broadcastChannel.close();

    this.$eventBus.off("hub-auth-success");
    this.$eventBus.off("auth-success");
    this.$eventBus.off("logout");
  },

  watch: {
    "$route.query.mkt_plus_auth": {
      immediate: true,

      handler(token) {
        this.handleMktPlusAuth(token);
      },
    },

    updateTick() {
      this.checkNews();
    },

    updateFirebaseTokenVar() {
      this.checkPermissionForFB();
    },
  },

  methods: {
    ...mapActions("websocket", {
      connect: "connect",
    }),

    removeLoadingScreen() {
      this.$nextTick(() => {
        const appLoading = document.getElementById("loading-screen");

        if (appLoading) appLoading.style.display = "none";
      });
    },

    setFavicon() {
      const faviconLink = document.getElementById("dinamic-favicon");

      if (!faviconLink) return;

      faviconLink.href = this.platform.logos.favicon || "/favicon.ico";
    },

    handleMktPlusAuth(token) {
      if (!token) {
        localStorage.removeItem("mktPlusToken");

        return;
      }

      const currentToken = localStorage.getItem("mktPlusToken");

      if (currentToken === token) return;

      localStorage.setItem("mktPlusToken", token);

      this.$store
        .dispatch("app/authenticateMktPlus", token)
        .then(() => {
          this.$store.commit("firebase/setUpdateToken");

          if (this.role == "salesRep") {
            this.$router.push("/omnichannel");
          } else {
            this.$router.push("/");
          }
        })
        .catch(() => {
          localStorage.removeItem("mktPlusToken");

          this.$bvToast.toast("Token inválido ou expirado", {
            title: `Erro ao autenticar por MKT Plus`,
            autoHideDelay: 3000,
            variant: "danger",
            toaster: "b-toaster-top-left",
            solid: true,
          });
        });
    },

    setNotificationOnStore(notification) {
      this.$store.commit("firebase/setHasNotificationBullet", true);

      if (this.isShowingNotification) return;

      this.$store.commit("firebase/setCurrentNotification", notification);
    },

    cleanStates() {
      this.$store.getters["opportunityStore/resetState"];
      this.$store.commit("externalPhone/RESET_STATE");
      this.$store.dispatch("modalCarousel/changeShowCarousel", false);

      this.$store.commit("firebase/clearCurrentNotification");
      this.$store.commit("firebase/setIsShowingNotification", false);
    },

    addMessageListener() {
      this.broadcastChannel = new BroadcastChannel("sw-messages");

      this.broadcastChannel.onmessage = (event) => {
        this.setNotificationOnStore(event.data);
      };
    },

    getAudiosReady() {
      if (this.isReadyAudio) {
        return;
      }

      this.$store.commit("externalPhone/TRIGGER_USER_INTERACTION");
    },

    initialSetup() {
      const { skin, skinClasses } = useAppConfig();

      if (skin.value === "dark") document.body.classList.add("dark-layout");

      store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);

      this.skinClasses = skinClasses;
    },

    checkNews() {
      const currentPath = window.location.pathname;

      if (this.allWarnings && currentPath != "/login") {
        const expirationDate = new Date();
        expirationDate.setFullYear(expirationDate.getFullYear() + 10);

        let warning;

        this.allWarnings.forEach((element) => {
          const currentDate = moment();
          const warningExpiration = moment(element.expiration, "DD/MM/YYYY");
          const hasPassed = warningExpiration.isSameOrBefore(currentDate);

          warning = localStorage.getItem(element.id);
          this.warning = element;

          if (!warning) {
            localStorage.setItem(element.id, true);

            if (!hasPassed) {
              this.$store.commit("newsWarning/CHANGE_NEWS_MODAL_CONDITION", true);
            }
          }
        });
      }
    },

    addGtag() {
      let script = document.createElement("script");

      script.textContent = `
        try {
          window.dataLayer = window.dataLayer || [];
          function gtag() {
            dataLayer.push(arguments);
          }
          gtag("js", new Date());
          gtag("config", "G-5E21DWH9DR");
        } catch (error) {
          console.error('Erro ao executar o Google Tag Manager:', error);
        }
      `;

      document.head.appendChild(script);
    },

    addClarity() {
      try {
        if (document.readyState === "loading") {
          document.addEventListener("DOMContentLoaded", () => this.initializeClarity());
        } else {
          this.initializeClarity();
        }
      } catch (error) {
        console.error("An error occurred while setting up Clarity: ", error);
      }
    },

    initializeClarity() {
      try {
        var script = document.createElement("script");
        script.type = "text/javascript";
        script.innerHTML = `
          (function (c, l, a, r, i, t, y) {
            c[a] = c[a] || function () {
              (c[a].q = c[a].q || []).push(arguments);
            };
            t = l.createElement(r);
            t.async = 1;
            t.src = "https://www.clarity.ms/tag/" + i;
            y = l.getElementsByTagName(r)[0];
            y.parentNode.insertBefore(t, y);
          })(window, document, "clarity", "script", "ldhn5jv14v");
        `;
        document.head.appendChild(script);
      } catch (error) {
        console.error("An error occurred while inserting the Clarity script: ", error);
      }
    },

    getDeviceType() {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;

      if (/android/i.test(userAgent)) {
        return "Android";
      }

      if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        return "Ios";
      }

      return "Desktop";
    },

    disconnectOmnichannelWebSocket() {
      const existingSocket = this.$store.state.omnichannelSocket.websocket || this.omnichannelSocket;

      if (existingSocket) {
        existingSocket.onclose = null;
        existingSocket.close();
      }
    },

    connectOmnichannelWebSocket() {
      if (!localStorage.getItem("token")) return;

      this.disconnectOmnichannelWebSocket();

      const url = `${process.env.VUE_APP_URL_WEBSOCKET}?x-access-token=${localStorage.getItem("token")}&platform_id=${this.platform?.id}`;
      this.omnichannelSocket = new WebSocket(url);

      this.$store.commit("omnichannelSocket/setConnectionStatus", "connecting");

      const timeout = setTimeout(() => this.omnichannelSocket.close(), 8000);

      this.omnichannelSocket.onopen = () => {
        clearTimeout(timeout);

        this.$store.commit("omnichannelSocket/setWebsocket", this.omnichannelSocket);
        this.$store.commit("omnichannelSocket/setConnectionStatus", "connected");
      };

      this.omnichannelSocket.onclose = () => this.handleOmnichannelSocketClose();

      this.omnichannelSocket.onmessage = (e) => {
        this.omniSocketReconnectAttempts = 0;

        let data;

        try {
          data = JSON.parse(e.data);
        } catch (error) {
          console.warn("Ignoring invalid Omnichannel WebSocket payload", e.data, error);
          return;
        }

        this.$store.dispatch("omnichannelSocket/handleMessage", data);
      };
    },

    handleOmnichannelSocketClose() {
      this.$store.commit("omnichannelSocket/setConnectionStatus", "disconnected");

      if (window.location.pathname === "/login") return;

      if (this.omniSocketReconnectAttempts >= 5) {
        this.$bvToast.toast(
          "O número máximo de tentativas de reconexão foi atingido. Verifique sua conexão com a internet, deslogue-se e tente novamente ou entre em contato com o suporte.",
          {
            title: "Conexão com o Omnichannel perdida",
            autoHideDelay: 4500,
            variant: "danger",
            toaster: "b-toaster-top-left",
            solid: true,
          },
        );

        return;
      }

      setTimeout(() => {
        this.omniSocketReconnectAttempts++;
        this.connectOmnichannelWebSocket();
      }, 4500);
    },

    connectWebSocket() {
      this.connect({
        autoReconnect: true,
        retries: 5,
        delay: 3000,

        heartbeat: true,
        heartbeatInterval: 30000,
        heartbeatMessage: "ping",
      });
    },

    async checkPermissionForFB() {
      try {
        if (!this.user?.id) return;

        this.hasSupport = window?.Notification;

        if (!this.hasSupport || !firebase.messaging.isSupported() || this.getDeviceType() === "Ios") {
          return;
        }

        const permission = await Notification.requestPermission();

        if (permission === "granted") {
          this.isPermissionGranted = true;

          if ("serviceWorker" in navigator) {
            navigator.serviceWorker
              .register("/firebase-messaging-sw.js")
              .then((registration) => {
                this.setFBToken(registration);
              })
              .catch((error) => {
                console.error("❌ Erro ao registrar Service Worker:", error);
              });
          }
        }
      } catch (error) {
        console.error("Erro ao verificar permissão para FB: ", error);
      }
    },

    async setFBToken(registration) {
      try {
        const fbToken = await getToken(this.$messaging, {
          vapidKey: "BD8rZjzUHP9JK2F_cXn-yqbVFRbnLqTvf72dDE5kMOi-6K_8fGEf6HpJoimoKWjgKrjQgzAzCmpHZeWBw_AmOFs",
          serviceWorkerRegistration: registration,
        });

        this.fbToken = fbToken;
        this.sendFBToken();
      } catch (error) {
        console.error("Erro ao obter token de push:", error);
      }
    },

    async sendFBToken() {
      if (!this.fbToken) {
        return;
      }

      const tokenData = {
        token: this.fbToken,
        device_type: this.getDeviceType(),
      };

      return this.$store
        .dispatch("firebase/registerToken", tokenData)
        .then(async () => {
          const messaging = await this.$messaging;

          onMessage(messaging, (payload) => {
            this.setNotificationOnStore(payload);
          });
        })
        .catch((error) => {
          console.log(error);
        });
    },

    setPlatform() {
      this.hasPlatformRequest = true;

      const url = window.location.host;

      this.$store
        .dispatch("whiteLabel/fetchPlatformByUrl", url)
        .then((response) => {
          this.$store.dispatch("whiteLabel/setPlatform", response.data);

          this.setFavicon();
          this.removeLoadingScreen();

          this.hasPlatformRequest = false;
        })
        .catch(() => {
          this.setDefaultPlatform();
        });
    },

    setDefaultPlatform() {
      this.hasPlatformRequest = true;

      const url =
        process.env.NODE_ENV == "production"
          ? "https://crm.skillpro.com.br"
          : "https://crm.sandbox.imlabs.com.br";

      this.$store
        .dispatch("whiteLabel/fetchPlatformByUrl", url)
        .then((response) => {
          this.$store.dispatch("whiteLabel/setPlatform", response.data);
        })
        .finally(() => {
          this.hasPlatformRequest = false;

          this.setFavicon();
          this.removeLoadingScreen();
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.loading-screen {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100dvw;
  height: 100dvh;
  background-color: #272a31;
  z-index: 9999;
}
</style>
